<template>
  <div class="onboarding-basic-info d-flex flex-column align-center">
    <div class="onboarding-basic-info__logo mb-5">
      <v-img
        :src="logoIcon"
        alt=""
        width="48"
        height="48"
        class="onboarding-basic-info__logo-img"
      />
    </div>

    <h1 class="text-h4 font-weight-bold mb-3">
      <template v-if="isPlatformFalc">{{ $t('onboarding.wizard.welcome.title-falc') }}</template>
      <template v-else>{{
        $t('onboarding.wizard.welcome.title', { platform: readablePlatformName })
      }}</template>
    </h1>

    <p class="text-body-1 text-medium-emphasis text-center mb-6 onboarding-basic-info__subtitle">
      {{ $t('onboarding.wizard.welcome.subtitle') }}
    </p>

    <v-card
      class="onboarding-basic-info__card pa-6 pa-md-8"
      rounded="xl"
      elevation="0"
      :style="{ border: '1px solid rgba(var(--v-border-color), 0.12)' }"
    >
      <v-form
        ref="formRef"
        v-model="isValid"
        @submit.prevent="handleContinue"
      >
        <label class="text-body-2 font-weight-medium d-flex align-center ga-2 mb-2">
          <v-icon
            size="16"
            color="primary"
          >
            mdi-web
          </v-icon>
          {{ $t('onboarding.wizard.basic-info.website-url') }}
          <span
            v-if="!noWebsite"
            class="text-error"
            >*</span
          >
        </label>
        <v-text-field
          v-model="websiteUrl"
          variant="outlined"
          density="comfortable"
          rounded="lg"
          :disabled="noWebsite"
          :placeholder="$t('onboarding.wizard.basic-info.website-url-placeholder')"
          :rules="validationRules"
          hide-details="auto"
        />

        <v-checkbox
          v-model="noWebsite"
          density="comfortable"
          color="primary"
          hide-details
          class="mt-2"
          :label="$t('onboarding.wizard.basic-info.no-website')"
          @update:model-value="noWebsiteTouched = true"
        />

        <p class="text-caption text-medium-emphasis mt-1 mb-6">
          {{
            noWebsite
              ? $t('onboarding.wizard.basic-info.no-website-hint')
              : $t('onboarding.wizard.basic-info.website-hint')
          }}
        </p>

        <div class="d-flex justify-end">
          <v-btn
            color="primary"
            size="large"
            rounded="lg"
            type="submit"
            :disabled="!canContinue"
            :loading="isSubmitting"
            class="text-uppercase font-weight-bold px-8"
          >
            {{ $t('onboarding.wizard.basic-info.continue') }}
          </v-btn>
        </div>
      </v-form>
    </v-card>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import logoIcon from '@/assets/img/logo-icon.png'
import { useBranding } from '@/composables/useBranding'

const { t } = useI18n()
const { readablePlatformName, isPlatformFalc } = useBranding()

const props = defineProps<{
  initialWebsite?: string
  /** Restores the "I don't have a website" choice when coming back to this step. */
  initialNoWebsite?: boolean
}>()

const emit = defineEmits<{
  continue: [data: { websiteUrl: string; noWebsite: boolean }]
}>()

const formRef = ref()
const isValid = ref(false)
const isSubmitting = ref(false)
const websiteUrl = ref(props.initialWebsite ?? '')
// Solo agents may not have a website — this skips the required URL and the scrape.
const noWebsite = ref(Boolean(props.initialNoWebsite))
const noWebsiteTouched = ref(false)

// The company can load after this step mounts; restore the saved choice unless
// the user already changed the checkbox.
watch(
  () => props.initialNoWebsite,
  (value) => {
    if (!noWebsiteTouched.value) noWebsite.value = Boolean(value)
  }
)

// When the user has no website, the field is optional so `continue` is always allowed.
const canContinue = computed(() => noWebsite.value || isValid.value)

const rules = {
  required: (v: string) =>
    !!v?.trim() || t('onboarding.wizard.basic-info.website-url') + ' is required',
  url: (v: string) => {
    if (!v) return true
    try {
      const urlStr = v.startsWith('http') ? v : `https://${v}`
      new URL(urlStr)
      return true
    } catch {
      return t('onboarding.wizard.basic-info.website-url-invalid')
    }
  },
}

const validationRules = computed(() => (noWebsite.value ? [] : [rules.required, rules.url]))

// Clear any typed URL when the box is ticked and re-validate so the form unblocks.
watch(noWebsite, (value) => {
  if (value) websiteUrl.value = ''
  void formRef.value?.validate()
})

const handleContinue = async () => {
  const { valid } = await formRef.value.validate()
  if (!valid) return

  isSubmitting.value = true
  emit('continue', {
    websiteUrl: noWebsite.value ? '' : websiteUrl.value.trim(),
    // Explicit "I have no website" — distinct from "URL not entered yet" so the
    // assistant/genaisis skips the website prompt and scrape entirely.
    noWebsite: noWebsite.value,
  })
}

defineExpose({
  stopLoading: () => {
    isSubmitting.value = false
  },
})
</script>

<style scoped>
.onboarding-basic-info {
  padding: 48px 24px;
}

.onboarding-basic-info__logo {
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: rgba(var(--v-theme-primary), 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
}

.onboarding-basic-info__logo-img {
  border-radius: 12px;
}

.onboarding-basic-info__subtitle {
  max-width: 480px;
  line-height: 1.6;
}

.onboarding-basic-info__card {
  width: 100%;
  max-width: 520px;
}
</style>
